import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { SECTORS, BENCHMARKS, FundHouse, Allocation } from "./shared";
import { useSwHorizonFeOverride, applySwHorizonOverride, SW_HORIZON_NAME } from "@/lib/swHorizonFeOverride";

interface SnapshotRow {
  fund_house_id: string;
  snapshot_at: string;
  holdings: Array<{ holding_name: string; holding_weight_pct: number }> | null;
}
interface HoldingRow { id: string; fund_house_id: string; holding_name: string; }
interface MappingRow { holding_id: string; cbam_sector_name: string; weight_pct: number; }

export default function BenchmarkSummary() {
  const [benchmark, setBenchmark] = useState(1);
  const [houses, setHouses] = useState<FundHouse[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedHouseIds, setSelectedHouseIds] = useState<string[]>([]);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // For transposed view (fund house names across all benchmarks)
  const [allHouses, setAllHouses] = useState<FundHouse[]>([]);
  const [allAllocations, setAllAllocations] = useState<Allocation[]>([]);
  const [selectedHouseNames, setSelectedHouseNames] = useState<string[]>([]);

  // Snapshot delta state
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  const [holdingsAll, setHoldingsAll] = useState<HoldingRow[]>([]);
  const [mappingsAll, setMappingsAll] = useState<MappingRow[]>([]);
  const [deltaHouseName, setDeltaHouseName] = useState<string>("");
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateA, setDateA] = useState<string>(monthAgo);
  const [dateB, setDateB] = useState<string>(today);

  useEffect(() => {
    let cancelled = false;
    const load = async (showLoading: boolean) => {
      if (showLoading) setLoading(true);
      const [hRes, aRes, allHRes, allARes, snapRes, fhhRes, mapRes] = await Promise.all([
        supabase.from("bm_fund_houses").select("*").eq("benchmark", benchmark).eq("is_active", true).order("sort_order").order("name"),
        supabase.from("bm_allocations").select("*").eq("benchmark", benchmark),
        supabase.from("bm_fund_houses").select("*").eq("is_active", true).order("name").order("benchmark"),
        supabase.from("bm_allocations").select("*"),
        supabase.from("bm_fund_house_holdings_snapshots").select("fund_house_id, snapshot_at, holdings"),
        supabase.from("bm_fund_house_holdings").select("id, fund_house_id, holding_name"),
        supabase.from("bm_holding_sector_mappings").select("holding_id, cbam_sector_name, weight_pct"),
      ]);
      if (cancelled) return;
      if (hRes.error) toast.error(hRes.error.message);
      if (aRes.error) toast.error(aRes.error.message);
      setHouses((hRes.data || []) as FundHouse[]);
      setAllocations((aRes.data || []) as Allocation[]);
      setAllHouses((allHRes.data || []) as FundHouse[]);
      setAllAllocations((allARes.data || []) as Allocation[]);
      setSnapshots((snapRes.data || []) as SnapshotRow[]);
      setHoldingsAll((fhhRes.data || []) as HoldingRow[]);
      setMappingsAll((mapRes.data || []) as MappingRow[]);
      setLoading(false);
    };
    const scheduleLoad = () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => load(false), 500);
    };
    load(true);

    const channel = supabase
      .channel(`benchmark-summary-${benchmark}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bm_fund_houses" }, scheduleLoad)
      .on("postgres_changes", { event: "*", schema: "public", table: "bm_holding_sector_mappings" }, scheduleLoad)
      .on("postgres_changes", { event: "*", schema: "public", table: "bm_fund_house_holdings" }, scheduleLoad)
      .subscribe();

    return () => {
      cancelled = true;
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      supabase.removeChannel(channel);
    };
  }, [benchmark]);

  // Reset selected houses when benchmark changes (default = all selected)
  useEffect(() => {
    setSelectedHouseIds(houses.map(h => h.id));
  }, [houses]);

  // Scottish Widows Horizon (BM 1-5): force FE-only sector allocation.
  // Swap manual bm_allocations rows for FE-derived rows and exclude houses
  // where FE can't be resolved.
  const swOverrideCurrent = useSwHorizonFeOverride(houses);
  const swOverrideAll = useSwHorizonFeOverride(allHouses);

  const effectiveAllocations = useMemo(
    () => applySwHorizonOverride(allocations, swOverrideCurrent),
    [allocations, swOverrideCurrent],
  );
  const effectiveAllAllocations = useMemo(
    () => applySwHorizonOverride(allAllocations, swOverrideAll),
    [allAllocations, swOverrideAll],
  );
  const visibleHouses = useMemo(
    () => houses.filter((h) => !swOverrideCurrent.excludedHouseIds.has(h.id)),
    [houses, swOverrideCurrent.excludedHouseIds],
  );

  const allocMap = useMemo(() => {
    const m = new Map<string, Allocation>();
    effectiveAllocations.forEach(a => m.set(`${a.sector_name}__${a.fund_house_id}`, a));
    return m;
  }, [effectiveAllocations]);

  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    visibleHouses.forEach(h => {
      totals[h.id] = SECTORS.reduce((sum, s) => sum + (allocMap.get(`${s}__${h.id}`)?.weight_pct || 0), 0);
    });
    return totals;
  }, [visibleHouses, allocMap]);

  const rowMeans = useMemo(() => {
    const means: Record<string, number> = {};
    SECTORS.forEach(s => {
      if (visibleHouses.length === 0) { means[s] = 0; return; }
      const sum = visibleHouses.reduce((acc, h) => acc + (allocMap.get(`${s}__${h.id}`)?.weight_pct || 0), 0);
      means[s] = sum / visibleHouses.length;
    });
    return means;
  }, [visibleHouses, allocMap]);

  const selectedRowMeans = useMemo(() => {
    const means: Record<string, number> = {};
    const selected = visibleHouses.filter(h => selectedHouseIds.includes(h.id));
    SECTORS.forEach(s => {
      if (selected.length === 0) { means[s] = 0; return; }
      const sum = selected.reduce((acc, h) => acc + (allocMap.get(`${s}__${h.id}`)?.weight_pct || 0), 0);
      means[s] = sum / selected.length;
    });
    return means;
  }, [visibleHouses, allocMap, selectedHouseIds]);

  const meanColumnTotal = useMemo(() => {
    return SECTORS.reduce((sum, s) => sum + (rowMeans[s] || 0), 0);
  }, [rowMeans]);

  const selectedMeanColumnTotal = useMemo(() => {
    return SECTORS.reduce((sum, s) => sum + (selectedRowMeans[s] || 0), 0);
  }, [selectedRowMeans]);

  // Transposed view: distinct fund-house names across benchmarks
  const houseNames = useMemo(() => {
    const set = new Set<string>();
    allHouses.forEach(h => set.add(h.name));
    return Array.from(set).sort();
  }, [allHouses]);

  useEffect(() => {
    if (selectedHouseNames.length === 0 && houseNames.length > 0) setSelectedHouseNames([houseNames[0]]);
  }, [houseNames]);

  const allAllocMap = useMemo(() => {
    const m = new Map<string, number>();
    effectiveAllAllocations.forEach(a => m.set(`${a.sector_name}__${a.fund_house_id}`, a.weight_pct));
    return m;
  }, [effectiveAllAllocations]);

  const toggleHouse = (id: string) => {
    setSelectedHouseIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ===== Snapshot delta calculations =====
  useEffect(() => {
    if (!deltaHouseName && houseNames.length > 0) setDeltaHouseName(houseNames[0]);
  }, [houseNames, deltaHouseName]);

  const holdingIdByFhName = useMemo(() => {
    const m = new Map<string, string>();
    holdingsAll.forEach(h => m.set(`${h.fund_house_id}__${h.holding_name.trim().toLowerCase()}`, h.id));
    return m;
  }, [holdingsAll]);

  const mappingsByHolding = useMemo(() => {
    const m = new Map<string, MappingRow[]>();
    mappingsAll.forEach(r => {
      const arr = m.get(r.holding_id) || [];
      arr.push(r);
      m.set(r.holding_id, arr);
    });
    return m;
  }, [mappingsAll]);

  const snapshotsByFh = useMemo(() => {
    const m = new Map<string, SnapshotRow[]>();
    snapshots.forEach(s => {
      const arr = m.get(s.fund_house_id) || [];
      arr.push(s);
      m.set(s.fund_house_id, arr);
    });
    m.forEach(arr => arr.sort((a, b) => a.snapshot_at.localeCompare(b.snapshot_at)));
    return m;
  }, [snapshots]);

  const deltaMatrix = useMemo(() => {
    const pickSnapshot = (fhId: string, isoDate: string): SnapshotRow | null => {
      const arr = snapshotsByFh.get(fhId);
      if (!arr || arr.length === 0) return null;
      const cutoff = `${isoDate}T23:59:59.999Z`;
      let chosen: SnapshotRow | null = null;
      for (const s of arr) {
        if (s.snapshot_at <= cutoff) chosen = s; else break;
      }
      return chosen;
    };
    const sectorWeights = (snap: SnapshotRow | null, fhId: string): Record<string, number> => {
      const out: Record<string, number> = {};
      if (!snap || !Array.isArray(snap.holdings)) return out;
      // The live bm_allocations recompute normalises each holding by the fund-house total
      // (holding_weight_pct / SUM(holding_weight_pct) * 100) BEFORE applying sector mappings. The
      // snapshot delta must do the same, or deltas computed from raw holding weights won't reconcile
      // with the live allocations.
      const totalW = snap.holdings.reduce((s, h) => s + (Number(h.holding_weight_pct) || 0), 0);
      if (totalW <= 0) return out;
      for (const h of snap.holdings) {
        const key = `${fhId}__${(h.holding_name || "").trim().toLowerCase()}`;
        const holdingId = holdingIdByFhName.get(key);
        if (!holdingId) continue;
        const maps = mappingsByHolding.get(holdingId) || [];
        const normW = ((Number(h.holding_weight_pct) || 0) / totalW) * 100;
        for (const m of maps) {
          const w = (normW * (Number(m.weight_pct) || 0)) / 100;
          out[m.cbam_sector_name] = (out[m.cbam_sector_name] || 0) + w;
        }
      }
      return out;
    };
    const result: Record<string, Record<number, { a: number; b: number; delta: number; hasA: boolean; hasB: boolean }>> = {};
    SECTORS.forEach(s => { result[s] = {} as Record<number, { a: number; b: number; delta: number; hasA: boolean; hasB: boolean }>; });
    BENCHMARKS.forEach(b => {
      const fhId = allHouses.find(h => h.name === deltaHouseName && h.benchmark === b)?.id;
      if (!fhId) {
        SECTORS.forEach(s => { result[s][b] = { a: 0, b: 0, delta: 0, hasA: false, hasB: false }; });
        return;
      }
      const snapA = pickSnapshot(fhId, dateA);
      const snapB = pickSnapshot(fhId, dateB);
      const wA = sectorWeights(snapA, fhId);
      const wB = sectorWeights(snapB, fhId);
      SECTORS.forEach(s => {
        const a = wA[s] || 0;
        const bv = wB[s] || 0;
        result[s][b] = { a, b: bv, delta: bv - a, hasA: !!snapA, hasB: !!snapB };
      });
    });
    return result;
  }, [allHouses, deltaHouseName, dateA, dateB, snapshotsByFh, holdingIdByFhName, mappingsByHolding]);

  const deltaTotals = useMemo(() => {
    const totals: Record<number, number> = {};
    BENCHMARKS.forEach(b => {
      totals[b] = SECTORS.reduce((sum, s) => sum + (deltaMatrix[s]?.[b]?.delta || 0), 0);
    });
    return totals;
  }, [deltaMatrix]);


  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Benchmark Top Level Summary</CardTitle>
              <CardDescription>Allocation (%) of each constituent fund house across CBAM Investment Sectors.</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Benchmark:</span>
              <Select value={String(benchmark)} onValueChange={(v) => setBenchmark(parseInt(v))}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BENCHMARKS.map(b => <SelectItem key={b} value={String(b)}>Benchmark {b}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-2">Mean of:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="min-w-[200px] justify-between">
                    <span className="truncate">
                      {selectedHouseIds.length === 0
                        ? "None selected"
                        : selectedHouseIds.length === visibleHouses.length
                          ? "All fund houses"
                          : `${selectedHouseIds.length} of ${visibleHouses.length} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-2" align="end">
                  <div className="flex justify-between mb-2 px-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => setSelectedHouseIds(visibleHouses.map(h => h.id))}>All</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => setSelectedHouseIds([])}>None</Button>
                  </div>
                  <div className="max-h-64 overflow-auto space-y-1">
                    {visibleHouses.map(h => (
                      <label key={h.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox checked={selectedHouseIds.includes(h.id)} onCheckedChange={() => toggleHouse(h.id)} />
                        <span className="truncate">{h.name}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px] sticky left-0 bg-background z-10">CBAM Investment Sector</TableHead>
                    {visibleHouses.map(h => (
                      <TableHead key={h.id} className="min-w-[120px] text-right">{h.name}</TableHead>
                    ))}
                    {visibleHouses.length > 0 && (
                      <>
                        <TableHead className="min-w-[120px] text-right bg-muted/40">Mean</TableHead>
                        <TableHead className="min-w-[140px] text-right bg-primary/10">
                          Selected Mean
                          <div className="text-[10px] font-normal text-muted-foreground">({selectedHouseIds.length} selected)</div>
                        </TableHead>
                      </>
                    )}
                    {visibleHouses.length === 0 && (
                      <TableHead className="text-muted-foreground italic">No fund houses configured</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SECTORS.map(sector => {
                    return (
                    <TableRow key={sector}>
                      <TableCell className="font-medium sticky left-0 bg-background z-10">{sector}</TableCell>
                      {visibleHouses.map(h => {
                        const a = allocMap.get(`${sector}__${h.id}`);
                        return (
                          <TableCell key={h.id} className="text-right tabular-nums">
                            {a ? `${a.weight_pct.toFixed(2)}%` : "—"}
                          </TableCell>
                        );
                      })}
                      {visibleHouses.length > 0 && (
                        <>
                          <TableCell className="text-right tabular-nums bg-muted/30 font-medium">
                            {(rowMeans[sector] || 0).toFixed(2)}%
                          </TableCell>
                          <TableCell className="text-right tabular-nums bg-primary/5 font-medium">
                            {selectedHouseIds.length === 0 ? "—" : `${(selectedRowMeans[sector] || 0).toFixed(2)}%`}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                    );
                  })}
                  {visibleHouses.length > 0 && (
                    <TableRow className="bg-muted/40 font-medium">
                      <TableCell className="sticky left-0 bg-muted/40 z-10">Column Total</TableCell>
                      {visibleHouses.map(h => (
                        <TableCell key={h.id} className="text-right tabular-nums">
                          {(columnTotals[h.id] || 0).toFixed(2)}%
                        </TableCell>
                      ))}
                      <TableCell className="text-right tabular-nums bg-muted/50">
                        {meanColumnTotal.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right tabular-nums bg-primary/10">
                        {selectedHouseIds.length === 0 ? "—" : `${selectedMeanColumnTotal.toFixed(2)}%`}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Fund House Across Benchmarks</CardTitle>
              <CardDescription>Allocation (%) of a single fund house across all five benchmarks.</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Fund House:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="min-w-[220px] justify-between">
                    <span className="truncate">
                      {selectedHouseNames.length === 0
                        ? "None selected"
                        : selectedHouseNames.length === houseNames.length
                          ? "All fund houses"
                          : `${selectedHouseNames.length} of ${houseNames.length} selected`}
                    </span>
                    <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-2" align="end">
                  <div className="flex justify-between mb-2 px-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => setSelectedHouseNames(houseNames)}>All</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs"
                      onClick={() => setSelectedHouseNames([])}>None</Button>
                  </div>
                  <div className="max-h-64 overflow-auto space-y-1">
                    {houseNames.map(n => (
                      <label key={n} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                        <Checkbox checked={selectedHouseNames.includes(n)} onCheckedChange={() => {
                          setSelectedHouseNames(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n]);
                        }} />
                        <span className="truncate">{n}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px] sticky left-0 bg-background z-10">CBAM Investment Sector</TableHead>
                    {BENCHMARKS.map(b => (
                      <TableHead key={b} className="min-w-[120px] text-right">Benchmark {b}</TableHead>
                    ))}
                    
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedHouseNames.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2 + BENCHMARKS.length} className="text-center text-muted-foreground py-8">
                        Select at least one fund house
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedHouseNames.length > 0 && (() => {
                    const avgBySector: Record<string, Record<number, number | null>> = {};
                    SECTORS.forEach(sector => {
                      avgBySector[sector] = {};
                      BENCHMARKS.forEach(b => {
                        const vals: number[] = [];
                        selectedHouseNames.forEach(houseName => {
                          const fhId = allHouses.find(h => h.name === houseName && h.benchmark === b)?.id;
                          if (fhId) vals.push(allAllocMap.get(`${sector}__${fhId}`) || 0);
                        });
                        avgBySector[sector][b] = vals.length ? vals.reduce((a, v) => a + v, 0) / vals.length : null;
                      });
                    });
                    return [
                      <TableRow key="avg-header" className="bg-primary/10">
                        <TableCell className="font-semibold sticky left-0 bg-primary/10 z-10">
                          Average across selected ({selectedHouseNames.length})
                        </TableCell>
                        {BENCHMARKS.map(b => <TableCell key={b} />)}
                      </TableRow>,
                      ...SECTORS.map(sector => {
                        return (
                        <TableRow key={`avg-${sector}`}>
                          <TableCell className="pl-6 sticky left-0 bg-background z-10">{sector}</TableCell>
                          {BENCHMARKS.map(b => {
                            const v = avgBySector[sector][b];
                            return (
                              <TableCell key={b} className="text-right tabular-nums">
                                {v == null ? "—" : `${v.toFixed(2)}%`}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        );
                      }),
                      <TableRow key="avg-total" className="bg-muted/40 font-medium">
                        <TableCell className="pl-6 sticky left-0 bg-muted/40 z-10">Column Total</TableCell>
                        {BENCHMARKS.map(b => {
                          const total = SECTORS.reduce((sum, s) => sum + (avgBySector[s][b] || 0), 0);
                          const anyVal = SECTORS.some(s => avgBySector[s][b] != null);
                          return (
                            <TableCell key={b} className="text-right tabular-nums">
                              {anyVal ? `${total.toFixed(2)}%` : "—"}
                            </TableCell>
                          );
                        })}
                      </TableRow>,
                    ];
                  })()}


                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Snapshot Delta — Fund House Across Benchmarks</CardTitle>
              <CardDescription>
                Change in CBAM sector allocation (%) between two snapshot dates for a single fund house, across all five benchmarks.
                Computed from snapshot holdings and current sector mappings.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-muted-foreground">Fund House:</span>
              <Select value={deltaHouseName} onValueChange={setDeltaHouseName}>
                <SelectTrigger className="w-[220px]"><SelectValue placeholder="Select fund house" /></SelectTrigger>
                <SelectContent>
                  {houseNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground ml-2">From:</span>
              <Input type="date" value={dateA} onChange={(e) => setDateA(e.target.value)} className="w-[160px]" />
              <span className="text-sm text-muted-foreground">To:</span>
              <Input type="date" value={dateB} onChange={(e) => setDateB(e.target.value)} className="w-[160px]" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="overflow-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[220px] sticky left-0 bg-background z-10">CBAM Investment Sector</TableHead>
                    {BENCHMARKS.map(b => (
                      <TableHead key={b} className="min-w-[120px] text-right">Benchmark {b} Δ</TableHead>
                    ))}
                    
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {SECTORS.map(sector => {
                    return (
                    <TableRow key={sector}>
                      <TableCell className="font-medium sticky left-0 bg-background z-10">{sector}</TableCell>
                      {BENCHMARKS.map(b => {
                        const cell = deltaMatrix[sector]?.[b];
                        if (!cell || (!cell.hasA && !cell.hasB)) {
                          return <TableCell key={b} className="text-right tabular-nums text-muted-foreground">—</TableCell>;
                        }
                        const d = cell.delta;
                        const sign = d > 0 ? "+" : "";
                        const color = Math.abs(d) < 0.005
                          ? "text-muted-foreground"
                          : d > 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400";
                        return (
                          <TableCell key={b} className={cn("text-right tabular-nums", color)}>
                            {`${sign}${d.toFixed(2)}%`}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    );
                  })}
                  <TableRow className="bg-muted/40 font-medium">
                    <TableCell className="sticky left-0 bg-muted/40 z-10">Net Δ</TableCell>
                    {BENCHMARKS.map(b => {
                      const t = deltaTotals[b] || 0;
                      const sign = t > 0 ? "+" : "";
                      return (
                        <TableCell key={b} className="text-right tabular-nums">
                          {`${sign}${t.toFixed(2)}%`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
